import { z } from 'zod';
import { ConnectionSchema, BrandingSchema, IntegrationsSchema, ThemeConfigSchema } from './admin-config';

/**
 * Schema for `GET /api/config/public`. A subset of the full admin
 * config that is safe to expose to unauthenticated browser clients.
 *
 * The backend builds this in `getPublicConfig()`. The frontend
 * `loadPublicConfig()` helper fetches and validates it on first paint.
 */

export const PublicConnectionSchema = ConnectionSchema;
export const PublicBrandingSchema = BrandingSchema;

/** Public auth fields — only client IDs (never secrets) and feature flags. */
export const PublicAuthSchema = z.object({
  googleClientId: z.string(),
  microsoftClientId: z.string(),
  githubClientId: z.string(),
  appUrl: z.string(),
  googleEnabled: z.boolean(),
  githubEnabled: z.boolean(),
  microsoftEnabled: z.boolean(),
});
export type PublicAuth = z.infer<typeof PublicAuthSchema>;

/** Public AI fields — model name + "do we have a key?" boolean, never the key. */
export const PublicAiSchema = z.object({
  intentProvider: z.string(),
  openaiModel: z.string(),
  hasApiKey: z.boolean(),
  // Optional per-provider model + presence pills so the UI can show
  // which providers are configured at a glance. Pre-Groq-Batch-2
  // payloads omit these fields, so they're all optional and parsing
  // stays backward-compatible.
  groqModel: z.string().optional(),
  anthropicModel: z.string().optional(),
  /**
   * `pending-sovereign-ai-tier` — Ollama model pin + presence
   * indicator. `hasOllamaKey` is always optional here since the
   * sovereign tier authenticates via network policy, not bearer
   * tokens — `true` means "operator has configured a shared-secret
   * proxy" and `false` (or absent) means "no proxy in front of the
   * daemon". Either way, the daemon URL itself is operator-
   * controlled and never exposed to the SPA.
   */
  ollamaModel: z.string().optional(),
  hasOpenaiKey: z.boolean().optional(),
  hasGroqKey: z.boolean().optional(),
  hasAnthropicKey: z.boolean().optional(),
  hasOllamaKey: z.boolean().optional(),
  // Per-feature routing surface — empty string = "use intentProvider".
  explainProvider: z.string().optional(),
  healingProvider: z.string().optional(),
  debugProvider: z.string().optional(),
  rcaProvider: z.string().optional(),
  /** Phase 11 — per-feature override for the chip strip's polish call. */
  suggestActionsProvider: z.string().optional(),
  /** `defer-llm-tool-use` — operator kill-switch + per-feature provider. */
  toolUseEnabled: z.boolean().optional(),
  toolUseProvider: z.string().optional(),
  /** `defer-debug-vision` — operator kill-switch + per-feature provider. */
  visionEnabled: z.boolean().optional(),
  visionProvider: z.string().optional(),
});
export type PublicAi = z.infer<typeof PublicAiSchema>;

/** Public billing fields — publishable key only. */
export const PublicBillingSchema = z.object({
  stripePublishableKey: z.string(),
  hasStripe: z.boolean(),
});
export type PublicBilling = z.infer<typeof PublicBillingSchema>;

/** Public observability — frontend Sentry DSN only. */
export const PublicObservabilitySchema = z.object({
  sentryDsnFrontend: z.string(),
  sentryEnvironment: z.string(),
  sentrySampleRate: z.number(),
  sentryTracesSampleRate: z.number(),
});
export type PublicObservability = z.infer<typeof PublicObservabilitySchema>;

export const PublicRunnerSchema = z.object({
  browserTier: z.string(),
  maxConcurrentSessions: z.number(),
  defaultViewport: z.object({ width: z.number(), height: z.number() }),
});
export type PublicRunner = z.infer<typeof PublicRunnerSchema>;

/**
 * Public security knobs — currently only the two flow-builder idle
 * minute counters. Never expose cloud-browser caps here; they are
 * operator-only and the runner reads them server-side.
 */
export const PublicSecuritySchema = z.object({
  flowBuilderIdleWarnMinutes: z.number().int().min(1).max(120),
  flowBuilderIdleLogoutMinutes: z.number().int().min(2).max(180),
});
export type PublicSecurity = z.infer<typeof PublicSecuritySchema>;

/**
 * Notifications — only the bits the SPA needs to render the
 * preference center (e.g. "is web push available?", "is marketing
 * opt-in offered at all?"). Never expose VAPID private keys or
 * unsubscribe HMAC secret here; those stay server-side.
 */
export const PublicNotificationsSchema = z.object({
  enabled: z.boolean(),
  webPushEnabled: z.boolean(),
  webPushVapidPublicKey: z.string(),
  marketingEnabled: z.boolean(),
});
export type PublicNotifications = z.infer<typeof PublicNotificationsSchema>;

export const PublicReportingSchema = z.object({
  enabled: z.boolean(),
  branding: z.object({
    reportName: z.string(),
    logoUrl: z.string(),
    companyName: z.string(),
    primaryColor: z.string(),
    accentColor: z.string(),
  }),
  sections: z.array(z.string()),
  screenshots: z.object({
    captureOnFailure: z.boolean(),
    captureOnPass: z.boolean(),
    quality: z.number(),
    format: z.string(),
  }),
  retention: z.object({
    maxReportsPerFlow: z.number(),
    maxAgeDays: z.number(),
  }),
  emailNotification: z.object({
    trigger: z.string(),
    recipients: z.array(z.string()),
    attachReport: z.boolean(),
  }),
});
export type PublicReporting = z.infer<typeof PublicReportingSchema>;

/**
 * No-code program kill-switches surfaced publicly so the flow-builder
 * can hide the Describe tab / AI refine button when the operator turns
 * them off — no admin round-trip needed. Values are not secrets;
 * exposing them only affects which authoring path is visible.
 */
export const PublicNoCodeDescribeModeSchema = z.object({
  enabled: z.boolean(),
  aiRefine: z.object({ enabled: z.boolean() }),
  /**
   * Phase 9.x · `defer-describe-auto-promote` — when true, the editor's
   * `useRunController` persists the runner's healed selector onto
   * `step.targetMeta.promotedSelector` after a successful describe-
   * mode run. Optional during rollout; the SPA defaults to `true`
   * when the field is absent (matches the as-shipped behaviour).
   */
  autoPromote: z.object({ enabled: z.boolean() }).optional(),
});
export type PublicNoCodeDescribeMode = z.infer<typeof PublicNoCodeDescribeModeSchema>;

// Phase 10 — same pattern as describeMode: surface the master switch
// for the natural-language step-authoring textarea so the editor can
// hide the input (and skip every `/api/step-parse` call) when the
// operator has turned it off.
export const PublicNoCodeNaturalLanguageAuthoringSchema = z.object({
  enabled: z.boolean(),
});
export type PublicNoCodeNaturalLanguageAuthoring = z.infer<typeof PublicNoCodeNaturalLanguageAuthoringSchema>;

// Phase 11 — next-action chip strip. Three knobs surfaced publicly so
// the SuggestionStrip can hide itself, skip the polish call, or clamp
// its load-more ceiling without an admin round-trip.
export const PublicNoCodeSuggestActionsSchema = z.object({
  enabled: z.boolean(),
  aiPolish: z.object({ enabled: z.boolean() }),
  /** Optional during rollout — SPA falls back to 25 when absent. */
  maxElements: z.number().int().positive().max(50).optional(),
  /** Phase 11.3 — opt-in global panel pinned to the top of the
   *  canvas. Optional during rollout — older API builds without
   *  Phase 11.3 still validate; the SPA falls back to `disabled`. */
  globalPanel: z.object({ enabled: z.boolean() }).optional(),
  /** `defer-server-side-smart-actions` (Phase 11.x) — opt-in
   *  server-side smart-action classifier. Optional during rollout
   *  — older API builds without the field validate; the SPA falls
   *  back to `disabled` (client-only). */
  serverSide: z.object({ enabled: z.boolean() }).optional(),
  /**
   * Phase 11.4–11.5 — suggestion-panel layout version.
   *
   *   - `v1` (default) — legacy flat chip strip. `SuggestionStrip`
   *     renders the existing three-section "Suggested / AI /
   *     Control flow" UI.
   *   - `v2` (Phase 11.4) — grouped element-row layout. Smart
   *     suggestions promote above the basic strip, basic chips
   *     collapse into one row per element with `[Click]` /
   *     `[Type]` / `[Verify]` verb buttons, and rows cluster under
   *     ARIA landmark headers (Main / Header / Footer / etc.).
   *     Kept available for rollback A/B; superseded by `v3`.
   *   - `v3` (Phase 11.5) — quick-picks + browse-all modal.
   *     Replaces enumeration: top 5 context-aware rows render
   *     inline; the long tail lives behind a `Browse all N
   *     elements →` button that opens a searchable modal. Verify
   *     verb uses the rich `VerifyAssertionPicker` (all 10
   *     assertion kinds, inline value inputs, pre-fill from
   *     element data).
   *
   * Optional during rollout — older API builds that don't ship
   * the field validate and the SPA falls back to `'v1'`.
   */
  layoutVersion: z.enum(['v1', 'v2', 'v3']).optional(),
  /**
   * `pending-suggestion-panel-v3-canary` v0 — when `true` the
   * editor emits a one-shot `suggestion_panel_v3_first_mount`
   * telemetry event the first time a user lands on v3 so we can
   * audit the canary rollout. Optional during rollout — older API
   * builds default `false` and the SPA stays silent.
   */
  emitCanaryTelemetry: z.boolean().optional(),
});
export type PublicNoCodeSuggestActions = z.infer<typeof PublicNoCodeSuggestActionsSchema>;

/**
 * `pending-runjs-step` (2026-05-20) — public surface for the inline
 * `runJS()` step (browser-context code escape hatch). The SPA reads
 * `enabled` to decide whether to render the step-palette tile; when
 * off the API also refuses to save a body, but hiding the tile makes
 * the surface honest at edit time instead of failing on save. Only
 * the master switch is public — the `maxTimeoutMs` / `maxCodeBytes`
 * envelope stays admin-side because the editor enforces those at
 * Monaco's input layer using the inline preview API.
 */
export const PublicNoCodeRunJsSchema = z.object({
  enabled: z.boolean(),
});
export type PublicNoCodeRunJs = z.infer<typeof PublicNoCodeRunJsSchema>;

export const PublicNoCodeSchema = z.object({
  describeMode: PublicNoCodeDescribeModeSchema,
  // Optional during rollout — older API builds without Phase 10 still
  // validate; the SPA defaults this to `{ enabled: true }` when absent.
  naturalLanguageAuthoring: PublicNoCodeNaturalLanguageAuthoringSchema.optional(),
  // Optional during rollout — older API builds without Phase 11 still
  // validate; the SPA defaults `enabled` + `aiPolish.enabled` to true
  // (the shipped behaviour) when absent.
  suggestActions: PublicNoCodeSuggestActionsSchema.optional(),
  // Optional during rollout — older API builds without
  // `pending-runjs-step` still validate; the SPA falls back to
  // `disabled` (the safe v1 default).
  runJs: PublicNoCodeRunJsSchema.optional(),
  agenticComposer: z.object({ enabled: z.boolean() }).optional(),
  /** `pending-locator-reliability` — kill switch + cache chip later. */
  locatorReliability: z
    .object({
      nearMissWaitBeforeLlm: z.boolean(),
      cache: z.object({ enabled: z.boolean() }).optional(),
    })
    .optional(),
});
export type PublicNoCode = z.infer<typeof PublicNoCodeSchema>;

/**
 * Phase 8.2 (`defer-runbutton-gate-wiring`) — surface the
 * approval-run-gate kill switch publicly so the editor knows
 * whether to consult `evaluateRunGate` before dispatching Run.
 * The actual policy + open-requests state lives in the approval
 * store; this flag is only the master switch.
 */
export const PublicApprovalRunGateSchema = z.object({
  enabled: z.boolean(),
});
export type PublicApprovalRunGate = z.infer<typeof PublicApprovalRunGateSchema>;

export const PublicApprovalSchema = z.object({
  runGate: PublicApprovalRunGateSchema,
});
export type PublicApproval = z.infer<typeof PublicApprovalSchema>;

/**
 * Session 5 of the P1 head-to-head roadmap — public surface of the
 * editor onboarding kill switches. The SPA reads
 * `firstFlowTutorial.enabled` once on first paint to decide whether
 * to auto-launch the 5-step guided tour for users who haven't
 * completed it yet (`users.onboarding_first_flow_completed = false`).
 * Defaults true so the as-shipped behaviour is preserved when the
 * field is missing.
 */
export const PublicOnboardingFirstFlowTutorialSchema = z.object({
  enabled: z.boolean(),
});
export type PublicOnboardingFirstFlowTutorial = z.infer<typeof PublicOnboardingFirstFlowTutorialSchema>;

export const PublicOnboardingSchema = z.object({
  firstFlowTutorial: PublicOnboardingFirstFlowTutorialSchema,
});
export type PublicOnboarding = z.infer<typeof PublicOnboardingSchema>;

/** v0.46 execution agent — read-only SPA gating (no secrets). */
export const PublicExecutionAgentSchema = z.object({
  enabled: z.boolean(),
  agentStaleSeconds: z.number().int().positive(),
  /** P0 fairness ceilings — advisory for operators/SPA; claim enforces server-side. */
  maxConcurrentJobsPerOrg: z.number().int().positive().optional(),
  maxConcurrentJobsPerProject: z.number().int().positive().optional(),
  maxQueuedJobsPerOrg: z.number().int().positive().optional(),
});
export type PublicExecutionAgent = z.infer<typeof PublicExecutionAgentSchema>;

export const PublicConfigSchema = z.object({
  connection: PublicConnectionSchema,
  branding: PublicBrandingSchema,
  environment: z.string(),
  auth: PublicAuthSchema,
  ai: PublicAiSchema,
  billing: PublicBillingSchema,
  observability: PublicObservabilitySchema,
  runner: PublicRunnerSchema,
  integrations: IntegrationsSchema.partial(),
  // Optional during rollout — older API builds that haven't shipped
  // Migration 054 won't return this slice; the SPA falls back to a
  // hardcoded 30-min logout when absent.
  security: PublicSecuritySchema.optional(),
  theme: ThemeConfigSchema,
  reporting: PublicReportingSchema,
  notifications: PublicNotificationsSchema.optional(),
  // Phase 9 — optional during rollout so older API builds without the
  // no-code config slice still validate; the SPA defaults both flags
  // to true (the shipped behaviour) when missing.
  noCode: PublicNoCodeSchema.optional(),
  /** `pending-project-knowledge` — compile kill switch. Optional during rollout. */
  knowledge: z.object({ enabled: z.boolean() }).optional(),
  /** `pending-quality-hq` — kill switch + default project open. Optional during rollout. */
  qualityHq: z
    .object({
      enabled: z.boolean(),
      defaultProjectOpen: z.enum(['quality', 'editor']),
    })
    .optional(),
  // Phase 8.2 — optional during rollout. SPA defaults to
  // `{ runGate: { enabled: false } }` when absent, i.e. the
  // pre-Phase-8.2 behaviour.
  approval: PublicApprovalSchema.optional(),
  // Session 5 of the P1 head-to-head roadmap — optional during
  // rollout so older API builds without the onboarding slice still
  // validate. SPA defaults `firstFlowTutorial.enabled` to true (the
  // v0 skeleton's as-shipped behaviour) when missing.
  onboarding: PublicOnboardingSchema.optional(),
  /** v0.46 — optional during rollout; SPA defaults enabled=false, agentStaleSeconds=90. */
  executionAgent: PublicExecutionAgentSchema.optional(),
});
export type PublicConfig = z.infer<typeof PublicConfigSchema>;

/**
 * SPA callers always append `/api/...` paths (e.g. `${base}/api/reports`).
 * `connection.apiBaseUrl` must therefore be the deployment *origin*
 * (`https://rynora.app`), never `…/api`. A mis-set admin_config row
 * (seen on UAT/Prod) produced `/api/api/...` → 404 for every SPA call.
 *
 * Strips a single trailing `/api` (with optional trailing slash) and
 * trailing slashes otherwise. Empty / whitespace input is returned as-is.
 */
export function normalizeApiBaseUrl(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (!trimmed) return raw;
  return trimmed.replace(/\/api$/i, '');
}
